import { Progress } from "@backstage/core-components";
import { alertApiRef, useApi } from "@backstage/core-plugin-api";
import { Spacer, useCurrentUser } from "@internal/plugin-utilities";
import {
  Button,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Theme,
} from "@material-ui/core";
import React, { ChangeEvent, useEffect, useState } from "react";
import { opsgenieApiRef } from "../../api";
import {
  useFilteredIncidentsWithPostMortems,
  useOpsgenieUsers,
} from "../../hooks";
import { OpsgenieIncident, OpsgenieUser } from "../../types";

const useStyles = makeStyles((_theme: Theme) => ({
  label: {
    marginBottom: "10px",
  },
  submit: {
    float: "right",
  },
}));

interface Props {}

export const NewPostMortemForm: React.FC<Props> = (): JSX.Element => {
  const classes = useStyles();

  const api = useApi(opsgenieApiRef);
  const alertApi = useApi(alertApiRef);

  const userResponse = useOpsgenieUsers();

  const currentUser = useCurrentUser();

  const [selectedIncident, setSelectedIncident] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<OpsgenieUser | undefined>(
    userResponse.results ? userResponse.results[0] : undefined
  );

  const incidents = useFilteredIncidentsWithPostMortems();

  useEffect(() => {
    if (userResponse.results) {
      const selected = userResponse.results.find((user: OpsgenieUser) => {
        return user.username === currentUser?.spec?.profile?.email;
      });

      setSelectedUser(selected);
    }
  }, [userResponse, currentUser, setSelectedUser]);

  const onIncidentChange = (event: ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    setSelectedIncident(value);
  };

  const onSelectedUserChange = (event: ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    if (userResponse.results) {
      setSelectedUser(
        userResponse.results.find((user: OpsgenieUser) => user.id === value)
      );
    }
  };

  const onSubmit = (event: any) => {
    event.preventDefault();
    if (selectedIncident === "" || !selectedUser) {
      alertApi.post({
        message: "You must supply an Incident ID and Incident Owner",
        severity: "error",
      });
      return;
    }

    (async () => {
      const request = await api.createPostMortem({
        incidentId: selectedIncident,
        userId: selectedUser.id,
      });

      if (request.status === "error") {
        alertApi.post({
          message: request.error
            ? request.error
            : "Could not determine the problem. Reach out to the Radiator administrators.",
          severity: "error",
        });
        return;
      }

      alertApi.post({
        message: `Post Mortem ID: ${request.data}`,
        severity: "success",
      });
    })();
  };

  if (incidents.loading) {
    return <Progress />;
  }

  return (
    <form id="post-mortem-form">
      <InputLabel className={classes.label}>
        Select the Incident for the Post Mortem:
      </InputLabel>
      <Select
        value={selectedIncident}
        onChange={onIncidentChange}
        required
        fullWidth
      >
        {incidents.results &&
          (incidents.results as OpsgenieIncident[]).map(
            (incident: OpsgenieIncident) => {
              return (
                <MenuItem key={incident.id} value={incident.id}>
                  {incident.message}: [{incident.id}]
                </MenuItem>
              );
            }
          )}
      </Select>
      <Spacer variant="double" />
      <InputLabel className={classes.label}>
        Select the User who owns the Post Mortem:
      </InputLabel>
      <Select
        value={selectedUser?.id || ""}
        onChange={onSelectedUserChange}
        fullWidth
        required
      >
        {userResponse.results &&
          userResponse.results.map((user: OpsgenieUser) => {
            return (
              <MenuItem key={user.id} value={user.id}>
                {user.fullName}
              </MenuItem>
            );
          })}
      </Select>
      <Spacer variant="double" />
      <Button
        type="submit"
        onClick={onSubmit}
        variant="outlined"
        className={classes.submit}
      >
        Submit Post Mortem
      </Button>
    </form>
  );
};
